import "./facts.css";
import { errorBanner, loadingBlock, pill } from "../shared/blocks";
import { createLifecycle } from "../shared/lifecycle";
import { h } from "../shared/safe";
import { approvedCard } from "./approvedCard";
import { conflictsCard } from "./conflictsCard";
import { gbpCard } from "./gbpCard";
import { historyCard } from "./historyCard";
import { targetsCard } from "./targetsCard";
import { unresolvedCard } from "./unresolvedCard";
import { createWorkspace, enc } from "./workspace";
import { clientWorkspaceNav } from "../client/workspaceNav";

// Per-client facts workspace: #/clients/:id/facts. One page, six cards, each loading and failing on its own:
// approved facts (read, edit with the revision guard), targets, unresolved information (visibly NOT approved), citation conflicts,
// GBP records (reference data; no GBP publishing) and the history of changes. Built with h(): everything from the server is text.

export async function clientFactsView(root: HTMLElement, params: Record<string, string>): Promise<void> {
  const shell = h("div", { class: "qf qf-cd fx" });
  root.replaceChildren(shell);
  const clientId = typeof params.id === "string" ? params.id : "";
  if (clientId === "") return void shell.append(errorBanner("This page needs a client. Go back to the Fleet and open one."));
  const life = createLifecycle(shell);
  const ws = createWorkspace(clientId, life);

  const title = h("h1", null, "Facts");
  const summary = h("div", { class: "fx-summary", "aria-live": "polite" });
  shell.append(
    h("div", { class: "topbar" }, title, h("div", { class: "btn-group" }, h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}` }, "← Client"), h("a", { class: "btn secondary", href: `#/clients/${enc(clientId)}/links`, id: "fx-to-links" }, "Created links"))),
    clientWorkspaceNav(clientId, "Business details"),
    summary,
    h(
      "nav",
      { class: "fx-jump", "aria-label": "Jump to a card" },
      ...[["fx-approved", "Approved facts"], ["fx-targets", "Targets"], ["fx-unresolved", "Unresolved"], ["fx-conflicts", "Conflicts"], ["fx-gbp", "GBP"], ["fx-history", "History"]].map(([id, label]) => h("a", { href: `#/clients/${enc(clientId)}/facts`, on: { click: (e: Event) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView?.({ block: "start" }); (document.getElementById(`${id}-h`) as HTMLElement | null)?.focus?.(); } } }, label)),
    ),
  );
  const cards = h("div", { class: "fx-cards" }, loadingBlock("Loading facts", 4));
  shell.append(cards);

  function paintSummary() {
    const f = ws.facts;
    if (!f) return;
    title.textContent = `Facts: ${typeof f.approved.business_name === "string" && f.approved.business_name ? f.approved.business_name : f.slug}`;
    summary.replaceChildren(pill(`Revision ${f.revision}`, "neutral"), pill(`${f.unresolvedOpen} unresolved (not approved)`, f.unresolvedOpen ? "warn" : "neutral"), pill(`${f.conflictsOpen} open citation ${f.conflictsOpen === 1 ? "conflict" : "conflicts"}`, f.conflictsOpen ? "warn" : "neutral"), pill(`${f.gbpProfiles} GBP ${f.gbpProfiles === 1 ? "record" : "records"} (reference only)`, "neutral"));
  }

  ws.onFacts(paintSummary);
  const first = await ws.refreshFacts();
  if (!life.alive()) return;
  if (!first) {
    cards.replaceChildren(errorBanner(ws.factsError ?? "The facts could not be loaded.", { retry: () => void clientFactsView(root, params) }));
    return;
  }
  paintSummary();
  cards.replaceChildren(approvedCard(ws), targetsCard(ws), unresolvedCard(ws), conflictsCard(ws), gbpCard(ws), historyCard(ws));
}
